import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { login } from "@/api/auth";
import { ErrorBanner } from "@/components/ErrorBanner";
import { Spinner } from "@/components/Spinner";
import { isAuthenticated, setToken } from "@/lib/auth";

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/";

  // Already logged in? Bounce to where the user wanted to go.
  if (isAuthenticated()) {
    navigate(next, { replace: true });
  }

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const mutation = useMutation({
    mutationFn: () => login({ username, password }),
    onSuccess: (data) => {
      setToken(data.access_token);
      navigate(next, { replace: true });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-600 text-white shadow-md">
            <svg viewBox="0 0 32 32" className="h-7 w-7" fill="none">
              <path
                d="M8 10h16M8 16h16M8 22h10"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h1 className="mt-3 text-lg font-semibold text-slate-900">
            CeroDeuda Lead Sync
          </h1>
          <p className="text-sm text-slate-500">Admin panel</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="card space-y-4 p-6"
          autoComplete="on"
        >
          <div>
            <label htmlFor="username" className="label">
              Username
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              autoFocus
              required
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="password" className="label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {mutation.isError && <ErrorBanner error={mutation.error} />}

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={mutation.isPending || !username || !password}
          >
            {mutation.isPending && <Spinner size="sm" />}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
}
